import { useController } from 'react-hook-form'

import { DoorMultiSelect } from '../../door-multi-select'
import { TextField, ToggleField } from '../../legacy-form-fields'
import { RemoteLockConnectionFormValues } from './remote-lock.schema'
import { useRemoteLockConnection } from './use-remote-lock-connection'

interface RemoteLockFieldsProps {
  buildingId: number
  disabled: boolean
  isConfigured: boolean
}

export const RemoteLockFields = ({ buildingId, disabled, isConfigured }: RemoteLockFieldsProps) => {
  // Note: same query key as the useConnection call in IntegrationCard — cache hit, not a
  // second request. See keyless-integrations.registry.ts / Salto Space for this pattern.
  const { doors, isLoadingDoors } = useRemoteLockConnection(buildingId)

  const authorizationCode = useController<RemoteLockConnectionFormValues, 'authorizationCode'>({
    name: 'authorizationCode',
  })
  const autoAssignToggle = useController<
    RemoteLockConnectionFormValues,
    'shouldAutoAssignApartmentDoorVisitor'
  >({
    name: 'shouldAutoAssignApartmentDoorVisitor',
  })
  const visitorDoors = useController<RemoteLockConnectionFormValues, 'visitorDefaults'>({
    name: 'visitorDefaults',
  })
  const staffDoors = useController<RemoteLockConnectionFormValues, 'staffDefaults'>({
    name: 'staffDefaults',
  })

  return (
    <div className="integration-configuration">
      <TextField
        errorLabel={authorizationCode.fieldState.error?.message}
        isError={Boolean(authorizationCode.fieldState.error)}
        label="Authorization Code"
        name={authorizationCode.field.name}
        placeholder="Paste the code obtained from RemoteLock"
        value={authorizationCode.field.value ?? ''}
        onBlur={authorizationCode.field.onBlur}
        onChange={authorizationCode.field.onChange}
      />
      <p className="integration-configuration__hint">
        Get this code by following RemoteLock&apos;s OAuth authorization steps (see the team wiki) —
        a new code is required every time this form is submitted, including updates.
      </p>

      <ToggleField
        id="shouldAutoAssignApartmentDoorVisitor"
        label="Autoassign Visitor's apartment door access"
        name={autoAssignToggle.field.name}
        value={autoAssignToggle.field.value}
        onChange={autoAssignToggle.field.onChange}
      />

      <div className="access-container">
        <h3 className="access-title">Visitor default doors</h3>
        <DoorMultiSelect
          disabled={disabled}
          isConfigured={isConfigured}
          isLoading={isLoadingDoors}
          options={doors}
          placeholder="Select doors..."
          value={visitorDoors.field.value ?? []}
          onChange={visitorDoors.field.onChange}
        />
      </div>

      <div className="access-container">
        <h3 className="access-title">Staff default doors</h3>
        <DoorMultiSelect
          disabled={disabled}
          isConfigured={isConfigured}
          isLoading={isLoadingDoors}
          options={doors}
          placeholder="Select doors..."
          value={staffDoors.field.value ?? []}
          onChange={staffDoors.field.onChange}
        />
      </div>
    </div>
  )
}
